/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Browser (Javascript) SDK", description = "Hightouch's browser Javascript SDK for collecting events", lastUpdatedDate = new Date(1738972800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Browser (Javascript) SDK",
        description: "Hightouch's browser Javascript SDK for collecting events",
        lastUpdatedDate: "Sat Feb 08 2025 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Installation",
          "depth": 0,
          "id": "installation"
        }, {
          "value": "API",
          "depth": 0,
          "id": "api"
        }, {
          "value": "Identify",
          "depth": 1,
          "id": "identify"
        }, {
          "value": "Track",
          "depth": 1,
          "id": "track"
        }, {
          "value": "Page",
          "depth": 1,
          "id": "page"
        }, {
          "value": "Group",
          "depth": 1,
          "id": "group"
        }, {
          "value": "Reset",
          "depth": 1,
          "id": "reset"
        }, {
          "value": "Single page applications",
          "depth": 0,
          "id": "single-page-applications"
        }, {
          "value": "Tracking emulated page views",
          "depth": 1,
          "id": "tracking-emulated-page-views"
        }, {
          "value": "QueryString API",
          "depth": 0,
          "id": "querystring-api"
        }, {
          "value": "Example",
          "depth": 1,
          "id": "example"
        }, {
          "value": "Configuration",
          "depth": 1,
          "id": "configuration"
        }, {
          "value": "Cross Domain Tracking",
          "depth": 0,
          "id": "cross-domain-tracking"
        }, {
          "value": "Autocollected fields",
          "depth": 0,
          "id": "autocollected-fields"
        }, {
          "value": "Session tracking",
          "depth": 0,
          "id": "session-tracking"
        }, {
          "value": "Campaign Attribution (UTM)",
          "depth": 0,
          "id": "campaign-attribution-utm"
        }, {
          "value": "Cookies and LocalStorage",
          "depth": 0,
          "id": "cookies-and-localstorage"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      h2: "h2",
      code: "code",
      pre: "pre",
      span: "span",
      ol: "ol",
      li: "li",
      h3: "h3",
      strong: "strong",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      a: "a",
      ul: "ul"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.p, {
        children: "The Browser SDK makes it easy to track events in webpages."
      }), "\n", _jsx(_components.h2, {
        id: "installation",
        children: "Installation"
      }), "\n", _jsxs(_components.p, {
        children: ["The Browser SDK can be installed by loading the Javascript via a ", _jsx(_components.code, {
          children: "script"
        }), " tag, or directly bundled via a ", _jsx(_components.code, {
          children: "npm"
        }), " package."]
      }), "\n", _jsxs(_components.p, {
        children: ["To install using a ", _jsx(_components.code, {
          children: "script"
        }), " tag, add the following snippet to your site, replacing WRITE_KEY with the write key from your Event Source:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-php-template",
          children: _jsxs(_components.span, {
            className: "xml",
            children: [_jsxs(_components.span, {
              className: "hljs-tag",
              children: ["<", _jsx(_components.span, {
                className: "hljs-name",
                children: "script"
              }), " ", _jsx(_components.span, {
                className: "hljs-attr",
                children: "type"
              }), "=", _jsx(_components.span, {
                className: "hljs-string",
                children: "\"text/javascript\""
              }), ">"]
            }), _jsxs(_components.span, {
              className: "javascript",
              children: ["\n!", _jsx(_components.span, {
                className: "hljs-keyword",
                children: "function"
              }), "(", _jsx(_components.span, {
                className: "hljs-params"
              }), "){", _jsx(_components.span, {
                className: "hljs-keyword",
                children: "var"
              }), " e=", _jsx(_components.span, {
                className: "hljs-variable language_",
                children: "window"
              }), ".", _jsx(_components.span, {
                className: "hljs-property",
                children: "htevents"
              }), "=", _jsx(_components.span, {
                className: "hljs-variable language_",
                children: "window"
              }), ".", _jsx(_components.span, {
                className: "hljs-property",
                children: "htevents"
              }), "||[];", _jsx(_components.span, {
                className: "hljs-keyword",
                children: "if"
              }), "(!e.", _jsx(_components.span, {
                className: "hljs-property",
                children: "initialize"
              }), ")", _jsx(_components.span, {
                className: "hljs-keyword",
                children: "if"
              }), "(e.", _jsx(_components.span, {
                className: "hljs-property",
                children: "invoked"
              }), ")", _jsx(_components.span, {
                className: "hljs-variable language_",
                children: "window"
              }), ".", _jsx(_components.span, {
                className: "hljs-property",
                children: "console"
              }), "&&", _jsx(_components.span, {
                className: "hljs-variable language_",
                children: "console"
              }), ".", _jsx(_components.span, {
                className: "hljs-property",
                children: "error"
              }), "&&", _jsx(_components.span, {
                className: "hljs-variable language_",
                children: "console"
              }), ".", _jsx(_components.span, {
                className: "hljs-title function_",
                children: "error"
              }), "(", _jsx(_components.span, {
                className: "hljs-string",
                children: "\"Hightouch snippet included twice.\""
              }), ");", _jsx(_components.span, {
                className: "hljs-keyword",
                children: "else"
              }), "{e.", _jsx(_components.span, {
                className: "hljs-property",
                children: "invoked"
              }), "=!", _jsx(_components.span, {
                className: "hljs-number",
                children: "0"
              }), ",e.", _jsx(_components.span, {
                className: "hljs-property",
                children: "methods"
              }), "=[", _jsx(_components.span, {
                className: "hljs-string",
                children: "\"trackSubmit\""
              }), ",", _jsx(_components.span, {
                className: "hljs-string",
                children: "\"trackClick\""
              }), ",", _jsx(_components.span, {
                className: "hljs-string",
                children: "\"trackLink\""
              }), ",", _jsx(_components.span, {
                className: "hljs-string",
                children: "\"trackForm\""
              }), ",", _jsx(_components.span, {
                className: "hljs-string",
                children: "\"pageview\""
              }), ",", _jsx(_components.span, {
                className: "hljs-string",
                children: "\"identify\""
              }), ",", _jsx(_components.span, {
                className: "hljs-string",
                children: "\"reset\""
              }), ",", _jsx(_components.span, {
                className: "hljs-string",
                children: "\"group\""
              }), ",", _jsx(_components.span, {
                className: "hljs-string",
                children: "\"track\""
              }), ",", _jsx(_components.span, {
                className: "hljs-string",
                children: "\"ready\""
              }), ",", _jsx(_components.span, {
                className: "hljs-string",
                children: "\"alias\""
              }), ",", _jsx(_components.span, {
                className: "hljs-string",
                children: "\"debug\""
              }), ",", _jsx(_components.span, {
                className: "hljs-string",
                children: "\"page\""
              }), ",", _jsx(_components.span, {
                className: "hljs-string",
                children: "\"once\""
              }), ",", _jsx(_components.span, {
                className: "hljs-string",
                children: "\"off\""
              }), ",", _jsx(_components.span, {
                className: "hljs-string",
                children: "\"on\""
              }), ",", _jsx(_components.span, {
                className: "hljs-string",
                children: "\"addSourceMiddleware\""
              }), ",", _jsx(_components.span, {
                className: "hljs-string",
                children: "\"addIntegrationMiddleware\""
              }), ",", _jsx(_components.span, {
                className: "hljs-string",
                children: "\"setAnonymousId\""
              }), ",", _jsx(_components.span, {
                className: "hljs-string",
                children: "\"addDestinationMiddleware\""
              }), "],e.", _jsx(_components.span, {
                className: "hljs-property",
                children: "factory"
              }), "=", _jsx(_components.span, {
                className: "hljs-keyword",
                children: "function"
              }), "(", _jsx(_components.span, {
                className: "hljs-params",
                children: "t"
              }), "){", _jsx(_components.span, {
                className: "hljs-keyword",
                children: "return"
              }), " ", _jsx(_components.span, {
                className: "hljs-keyword",
                children: "function"
              }), "(", _jsx(_components.span, {
                className: "hljs-params"
              }), "){", _jsx(_components.span, {
                className: "hljs-keyword",
                children: "var"
              }), " n=", _jsx(_components.span, {
                className: "hljs-title class_",
                children: "Array"
              }), ".", _jsx(_components.span, {
                className: "hljs-property",
                children: _jsx(_components.span, {
                  className: "hljs-keyword",
                  children: "prototype"
                })
              }), ".", _jsx(_components.span, {
                className: "hljs-property",
                children: "slice"
              }), ".", _jsx(_components.span, {
                className: "hljs-title function_",
                children: "call"
              }), "(", _jsx(_components.span, {
                className: "hljs-variable language_",
                children: "arguments"
              }), ");", _jsx(_components.span, {
                className: "hljs-keyword",
                children: "return"
              }), " n.", _jsx(_components.span, {
                className: "hljs-title function_",
                children: "unshift"
              }), "(t),e.", _jsx(_components.span, {
                className: "hljs-title function_",
                children: "push"
              }), "(n),e}};", _jsx(_components.span, {
                className: "hljs-keyword",
                children: "for"
              }), "(", _jsx(_components.span, {
                className: "hljs-keyword",
                children: "var"
              }), " t=", _jsx(_components.span, {
                className: "hljs-number",
                children: "0"
              }), ";t<e.", _jsx(_components.span, {
                className: "hljs-property",
                children: "methods"
              }), ".", _jsx(_components.span, {
                className: "hljs-property",
                children: "length"
              }), ";t++){", _jsx(_components.span, {
                className: "hljs-keyword",
                children: "var"
              }), " n=e.", _jsx(_components.span, {
                className: "hljs-property",
                children: "methods"
              }), "[t];e[n]=e.", _jsx(_components.span, {
                className: "hljs-title function_",
                children: "factory"
              }), "(n)}e.", _jsx(_components.span, {
                className: "hljs-property",
                children: "load"
              }), "=", _jsx(_components.span, {
                className: "hljs-keyword",
                children: "function"
              }), "(", _jsx(_components.span, {
                className: "hljs-params",
                children: "t,n"
              }), "){", _jsx(_components.span, {
                className: "hljs-keyword",
                children: "var"
              }), " o=", _jsx(_components.span, {
                className: "hljs-variable language_",
                children: "document"
              }), ".", _jsx(_components.span, {
                className: "hljs-title function_",
                children: "createElement"
              }), "(", _jsx(_components.span, {
                className: "hljs-string",
                children: "\"script\""
              }), ");o.", _jsx(_components.span, {
                className: "hljs-property",
                children: "type"
              }), "=", _jsx(_components.span, {
                className: "hljs-string",
                children: "\"text/javascript\""
              }), ",o.", _jsx(_components.span, {
                className: "hljs-property",
                children: "async"
              }), "=!", _jsx(_components.span, {
                className: "hljs-number",
                children: "0"
              }), ",o.", _jsx(_components.span, {
                className: "hljs-property",
                children: "src"
              }), "=", _jsx(_components.span, {
                className: "hljs-string",
                children: "\"https://cdn.hightouch-events.com/browser/release/v1-latest/events.min.js\""
              }), ";", _jsx(_components.span, {
                className: "hljs-keyword",
                children: "var"
              }), " r=", _jsx(_components.span, {
                className: "hljs-variable language_",
                children: "document"
              }), ".", _jsx(_components.span, {
                className: "hljs-title function_",
                children: "getElementsByTagName"
              }), "(", _jsx(_components.span, {
                className: "hljs-string",
                children: "\"script\""
              }), ")[", _jsx(_components.span, {
                className: "hljs-number",
                children: "0"
              }), "];r.", _jsx(_components.span, {
                className: "hljs-property",
                children: "parentNode"
              }), ".", _jsx(_components.span, {
                className: "hljs-title function_",
                children: "insertBefore"
              }), "(o,r),e.", _jsx(_components.span, {
                className: "hljs-property",
                children: "_loadOptions"
              }), "=n,e.", _jsx(_components.span, {
                className: "hljs-property",
                children: "_writeKey"
              }), "=t},e.", _jsx(_components.span, {
                className: "hljs-property",
                children: "SNIPPET_VERSION"
              }), "=", _jsx(_components.span, {
                className: "hljs-string",
                children: "\"0.0.1\""
              }), ",\ne.", _jsx(_components.span, {
                className: "hljs-title function_",
                children: "load"
              }), "(", _jsx(_components.span, {
                className: "hljs-string",
                children: "'WRITE_KEY'"
              }), ",{", _jsx(_components.span, {
                className: "hljs-attr",
                children: "apiHost"
              }), ":", _jsx(_components.span, {
                className: "hljs-string",
                children: "'us-east-1.hightouch-events.com'"
              }), "}),\ne.", _jsx(_components.span, {
                className: "hljs-title function_",
                children: "page"
              }), "()}}();\n"]
            }), _jsxs(_components.span, {
              className: "hljs-tag",
              children: ["</", _jsx(_components.span, {
                className: "hljs-name",
                children: "script"
              }), ">"]
            }), "\n"]
          })
        })
      }), "\n", _jsxs(_components.p, {
        children: ["To install using ", _jsx(_components.code, {
          children: "npm"
        }), ":"]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Install the SDK by running ", _jsx(_components.code, {
            children: "npm install @ht-sdks/events-sdk-js-browser"
          })]
        }), "\n", _jsx(_components.li, {
          children: "Import and initialize the SDK with the following:"
        }), "\n"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-jsx",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "import"
          }), " { ", _jsx(_components.span, {
            className: "hljs-title class_",
            children: "HtEventsBrowser"
          }), " } ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "from"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"@ht-sdks/events-sdk-js-browser\""
          }), ";\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "export"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "const"
          }), " htevents = ", _jsx(_components.span, {
            className: "hljs-title class_",
            children: "HtEventsBrowser"
          }), ".", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "load"
          }), "(\n  { ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "writeKey"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"WRITE_KEY\""
          }), " },\n  { ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "apiHost"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"us-east-1.hightouch-events.com\""
          }), " }\n);\n"]
        })
      }), "\n", _jsx(_components.h2, {
        id: "api",
        children: "API"
      }), "\n", _jsx(_components.h3, {
        id: "identify",
        children: "Identify"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "identify"
        }), " method is used to send an ", _jsx(_components.code, {
          children: "identify"
        }), " event."]
      }), "\n", _jsxs(_components.p, {
        children: ["If ", _jsx(_components.code, {
          children: "identify"
        }), " is called multiple times for the same user, the ", _jsx(_components.code, {
          children: "traits"
        }), " are\nmerged together."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method signature"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-css",
          children: ["htevents", _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".identify"
          }), "(userId, ", _jsx(_components.span, {
            className: "hljs-selector-attr",
            children: "[traits]"
          }), ", ", _jsx(_components.span, {
            className: "hljs-selector-attr",
            children: "[context]"
          }), ", ", _jsx(_components.span, {
            className: "hljs-selector-attr",
            children: "[callback]"
          }), ")\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method parameters"
        }), ":"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Parameter"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "userId"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The user's persistent ID."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "traits"
              })
            }), _jsx(_components.td, {
              children: "Object"
            }), _jsxs(_components.td, {
              children: ["Additional traits about the user, such as ", _jsx(_components.code, {
                children: "email"
              }), " and ", _jsx(_components.code, {
                children: "name"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "context"
              })
            }), _jsx(_components.td, {
              children: "Object"
            }), _jsxs(_components.td, {
              children: ["Overrides to values in the event ", _jsx(_components.code, {
                children: "context"
              }), ". By default, ", _jsx(_components.code, {
                children: "context"
              }), " contains information autocollected by the SDK."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "callback"
              })
            }), _jsx(_components.td, {
              children: "Function"
            }), _jsx(_components.td, {
              children: "A callback that's invoked after the event is sent."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "track",
        children: "Track"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "track"
        }), " method is used to send a ", _jsx(_components.code, {
          children: "track"
        }), " event."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method signature"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-css",
          children: ["htevents", _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".track"
          }), "(event, ", _jsx(_components.span, {
            className: "hljs-selector-attr",
            children: "[properties]"
          }), ", ", _jsx(_components.span, {
            className: "hljs-selector-attr",
            children: "[context]"
          }), ", ", _jsx(_components.span, {
            className: "hljs-selector-attr",
            children: "[callback]"
          }), ")\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method parameters"
        }), ":"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Parameter"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "event"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The event name (for example \"Checked Out\")."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "properties"
              })
            }), _jsx(_components.td, {
              children: "Object"
            }), _jsxs(_components.td, {
              children: ["Additional properties about the event, such as ", _jsx(_components.code, {
                children: "product_id"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "context"
              })
            }), _jsx(_components.td, {
              children: "Object"
            }), _jsxs(_components.td, {
              children: ["Overrides to values in the event ", _jsx(_components.code, {
                children: "context"
              }), ". By default, ", _jsx(_components.code, {
                children: "context"
              }), " contains information autocollected by the SDK."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "callback"
              })
            }), _jsx(_components.td, {
              children: "Function"
            }), _jsx(_components.td, {
              children: "A callback that's invoked after the event is sent."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "page",
        children: "Page"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "page"
        }), " method is used to send a ", _jsx(_components.code, {
          children: "page"
        }), " event."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method signature"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-css",
          children: ["htevents", _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".page"
          }), "(category, name, ", _jsx(_components.span, {
            className: "hljs-selector-attr",
            children: "[properties]"
          }), ", ", _jsx(_components.span, {
            className: "hljs-selector-attr",
            children: "[context]"
          }), ", ", _jsx(_components.span, {
            className: "hljs-selector-attr",
            children: "[callback]"
          }), ")\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method parameters"
        }), ":"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Parameter"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "category"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The page's category. For example, \"Docs\"."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "name"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The page's name. For example, \"Getting started\"."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "properties"
              })
            }), _jsx(_components.td, {
              children: "Object"
            }), _jsxs(_components.td, {
              children: ["Additional properties about the event, such as ", _jsx(_components.code, {
                children: "url"
              }), ". By default, the SDK autocollects properties such as page referrer."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "context"
              })
            }), _jsx(_components.td, {
              children: "Object"
            }), _jsxs(_components.td, {
              children: ["Overrides to values in the event ", _jsx(_components.code, {
                children: "context"
              }), ". By default, ", _jsx(_components.code, {
                children: "context"
              }), " contains information autocollected by the SDK."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "callback"
              })
            }), _jsx(_components.td, {
              children: "Function"
            }), _jsx(_components.td, {
              children: "A callback that's invoked after the event is sent."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "group",
        children: "Group"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "group"
        }), " method sends a ", _jsx(_components.code, {
          children: "group"
        }), " event."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method signature"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-css",
          children: ["htevents", _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".group"
          }), "(groupId, ", _jsx(_components.span, {
            className: "hljs-selector-attr",
            children: "[traits]"
          }), ", ", _jsx(_components.span, {
            className: "hljs-selector-attr",
            children: "[context]"
          }), ", ", _jsx(_components.span, {
            className: "hljs-selector-attr",
            children: "[callback]"
          }), ")\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method parameters"
        }), ":"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Parameter"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "groupId"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The id for the group."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "traits"
              })
            }), _jsx(_components.td, {
              children: "Object"
            }), _jsxs(_components.td, {
              children: ["Additional traits about the group, such as ", _jsx(_components.code, {
                children: "company_name"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "context"
              })
            }), _jsx(_components.td, {
              children: "Object"
            }), _jsxs(_components.td, {
              children: ["Overrides to values in the event ", _jsx(_components.code, {
                children: "context"
              }), ". By default, ", _jsx(_components.code, {
                children: "context"
              }), " contains information autocollected by the SDK."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "callback"
              })
            }), _jsx(_components.td, {
              children: "Function"
            }), _jsx(_components.td, {
              children: "A callback that's invoked after the event is sent."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "reset",
        children: "Reset"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "reset"
        }), " method resets the ", _jsx(_components.code, {
          children: "identify"
        }), " and ", _jsx(_components.code, {
          children: "group"
        }), " for the local session.\nSpecifically, it resets the ", _jsx(_components.code, {
          children: "anonymousId"
        }), ", ", _jsx(_components.code, {
          children: "userId"
        }), ", ", _jsx(_components.code, {
          children: "groupId"
        }), ", and ", _jsx(_components.code, {
          children: "traits"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "reset"
        }), " method should be called when users log out. This way, if the user\nlogs back in with another account, the userIds and traits from the different\nsessions remain separate."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method signature"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-scss",
          children: ["htevents", _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".reset"
          }), "()\n"]
        })
      }), "\n", _jsx(_components.h2, {
        id: "single-page-applications",
        children: "Single page applications"
      }), "\n", _jsx(_components.p, {
        children: "While Single Page Apps (SPAs) are great for many reasons, they do require some extra consideration in order to set up client-side tracking than with a traditional webpage."
      }), "\n", _jsxs(_components.p, {
        children: ["By default, the Hightouch htevents.js library doesn't generate or store the referrer value. Instead, the referrer value you see in the payload is the value returned by ", _jsx(_components.code, {
          children: "document.referrer"
        }), " directly from the browser, and the URL value is the canonical URL on the page."]
      }), "\n", _jsx(_components.p, {
        children: "When a user navigates between pages on an SPA website, there won't be a referrer because there is no concept of a new page since it's all a single page load. This means that the referrer will always be the same as it was on the first page call where someone was first directed to your site. However, in order to circumvent this, you can manually set the referrer and URL in your Hightouch calls by updating the context object."
      }), "\n", _jsx(_components.p, {
        children: "For example, a Page call with the referrer and URL manually set looks like this:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-js",
          children: ["htevents.", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "page"
          }), "({\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "referrer"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'https://hightouch.com/'"
          }), ",\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "url"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'https://hightouch.com/pricing/?ref=nav'"
          }), "\n})\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "A Track call with these fields manually updated looks like this:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-js",
          children: ["htevents.", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "track"
          }), "(", _jsx(_components.span, {
            className: "hljs-string",
            children: "'Example Event'"
          }), ", {}, {", _jsx(_components.span, {
            className: "hljs-attr",
            children: "page"
          }), ": {\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "referrer"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'https://hightouch.com/'"
          }), ",\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "url"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'https://hightouch.com/pricing/?ref=nav'"
          }), "\n}})\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "tracking-emulated-page-views",
        children: "Tracking emulated page views"
      }), "\n", _jsx(_components.p, {
        children: "Your application should update the URL in the address bar to emulate traditional webpage navigation. Full page requests aren't made in most of these instances since the resources are loaded on initial page load. This means that the Page call in the traditional htevents.js snippet won't fire again as a user navigates around your site."
      }), "\n", _jsxs(_components.p, {
        children: ["You should still place the snippet in the head of your site, but you should remove the Page call and fire it whenever you're emulating a page load. Hightouch recommends that you call ", _jsx(_components.a, {
          href: "/events/sdks/browser#page",
          children: "Page"
        }), " from the same block of logic that updates the view and URL path like below:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-js",
          children: [_jsx(_components.span, {
            className: "hljs-comment",
            children: "// The new view has been called to render"
          }), "\nhtevents.", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "page"
          }), "(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Home\""
          }), ")\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "To track more than the page field, pass those fields in as additional properties. Hightouch recommends that you use variables to set information about page properties, rather than hard-coding. In most SPA frameworks, you can automate this by attaching the Page call to the routing service."
      }), "\n", _jsx(_components.h2, {
        id: "querystring-api",
        children: "QueryString API"
      }), "\n", _jsxs(_components.p, {
        children: ["The Browser SDK can trigger ", _jsx(_components.code, {
          children: "track"
        }), " and ", _jsx(_components.code, {
          children: "identify"
        }), " events based on the URL query string. You can use this when tracking email click-throughs, social media clicks, and digital advertising."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Query parameters"
        }), ":"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Parameter"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Triggers"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "ajs_uid"
              })
            }), _jsxs(_components.td, {
              children: ["The ", _jsx(_components.code, {
                children: "userId"
              }), " for the ", _jsx(_components.code, {
                children: "identify"
              }), " event."]
            }), _jsxs(_components.td, {
              children: ["This triggers an ", _jsx(_components.code, {
                children: "identify"
              }), " event."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "ajs_event"
              })
            }), _jsxs(_components.td, {
              children: ["The ", _jsx(_components.code, {
                children: "name"
              }), " for the ", _jsx(_components.code, {
                children: "track"
              }), " event."]
            }), _jsxs(_components.td, {
              children: ["This triggers a ", _jsx(_components.code, {
                children: "track"
              }), " event."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "ajs_aid"
              })
            }), _jsxs(_components.td, {
              children: ["The ", _jsx(_components.code, {
                children: "anonymousId"
              }), " for the user."]
            }), _jsxs(_components.td, {
              children: ["This sets the ", _jsx(_components.code, {
                children: "anonymousId"
              }), ", by itself does not trigger any event."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "ajs_prop_<property>"
              })
            }), _jsxs(_components.td, {
              children: ["A property to set on the ", _jsx(_components.code, {
                children: "track"
              }), " event."]
            }), _jsxs(_components.td, {
              children: ["This sets a property on the resulting ", _jsx(_components.code, {
                children: "track"
              }), " event, by itself does not trigger any event."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "ajs_trait_<trait>"
              })
            }), _jsxs(_components.td, {
              children: ["A trait to set on the ", _jsx(_components.code, {
                children: "identity"
              }), " event."]
            }), _jsxs(_components.td, {
              children: ["This sets a trait on the resulting ", _jsx(_components.code, {
                children: "identify"
              }), " event, by itself does not trigger any event."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "example",
        children: "Example"
      }), "\n", _jsx(_components.p, {
        children: "The following URL:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-ini",
          children: ["https://hightouch.com/?", _jsx(_components.span, {
            className: "hljs-attr",
            children: "ajs_uid"
          }), "=", _jsx(_components.span, {
            className: "hljs-number",
            children: "123"
          }), "&ajs_event=Clicked%", _jsx(_components.span, {
            className: "hljs-number",
            children: "20"
          }), "Email&ajs_aid=abc123&ajs_prop_emailCampaign=First+Touch&ajs_trait_name=John\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "would result in these calls:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-js",
          children: ["htevents.", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "identify"
          }), "(", _jsx(_components.span, {
            className: "hljs-string",
            children: "'123'"
          }), ", { ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "name"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'John'"
          }), " });\nhtevents.", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "track"
          }), "(", _jsx(_components.span, {
            className: "hljs-string",
            children: "'Clicked Email'"
          }), ", { ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'emailCampaign'"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'First Touch'"
          }), " });\nhtevents.", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "user"
          }), "().", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "anonymousId"
          }), "(", _jsx(_components.span, {
            className: "hljs-string",
            children: "'abc123'"
          }), ");\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "configuration",
        children: "Configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "useQueryString"
        }), " option allows you to control the behavior of the query parameters. For example, you can entirely disable query string processing by setting ", _jsx(_components.code, {
          children: "useQueryString"
        }), " to ", _jsx(_components.code, {
          children: "false"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-js",
          children: ["htevents.", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "load"
          }), "(", _jsx(_components.span, {
            className: "hljs-string",
            children: "'WRITE_KEY'"
          }), ", {\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "useQueryString"
          }), ": ", _jsx(_components.span, {
            className: "hljs-literal",
            children: "false"
          }), "\n})\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "You can also keep query string processing on, but enforce validation rules. For example:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-js",
          children: ["htevents.", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "load"
          }), "(", _jsx(_components.span, {
            className: "hljs-string",
            children: "'WRITE_KEY'"
          }), ", {\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "useQueryString"
          }), ": {\n    ", _jsx(_components.span, {
            className: "hljs-comment",
            children: "// set a pattern for anonymous id"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "aid"
          }), ": ", _jsx(_components.span, {
            className: "hljs-regexp",
            children: "/([A-Z]{10})/"
          }), ",\n    ", _jsx(_components.span, {
            className: "hljs-comment",
            children: "// set a pattern for user id"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "uid"
          }), ": ", _jsx(_components.span, {
            className: "hljs-regexp",
            children: "/([A-Z]{6})/"
          }), "\n  }\n})\n"]
        })
      }), "\n", _jsx(_components.h2, {
        id: "cross-domain-tracking",
        children: "Cross Domain Tracking"
      }), "\n", _jsxs(_components.p, {
        children: ["Using the ", _jsx(_components.a, {
          href: "#querystring-api",
          children: "QueryString API"
        }), " you can easily track users across different domains. For example, if you have the following sites:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Site 1: ", _jsx(_components.code, {
            children: "www.landing-page.com"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Site 2: ", _jsx(_components.code, {
            children: "www.product-page.com"
          })]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["To track a user who starts on Site 1 and navigates to Site 2, you just need to pass the ", _jsx(_components.code, {
          children: "userId"
        }), " from Site 1 to Site 2 using the ", _jsx(_components.code, {
          children: "ajs_uid"
        }), " query parameter."]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-ini",
          children: ["https://www.product-page.com/?", _jsx(_components.span, {
            className: "hljs-attr",
            children: "ajs_uid"
          }), "=", _jsx(_components.span, {
            className: "hljs-number",
            children: "123"
          }), "\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["This will automatically trigger an ", _jsx(_components.code, {
          children: "identify"
        }), " event on Site 2 with ", _jsx(_components.code, {
          children: "userId: 123"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["If you want to track anonymous users across domains, you can access the ", _jsx(_components.code, {
          children: "anonymousId"
        }), " on Site 1 using:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-js",
          children: ["htevents.", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "user"
          }), "().", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "anonymousId"
          }), "()\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["You can then pass the ", _jsx(_components.code, {
          children: "anonymousId"
        }), " from Site 1 to Site 2 using the ", _jsx(_components.code, {
          children: "ajs_aid"
        }), " query parameter. Note that the ", _jsx(_components.code, {
          children: "ajs_aid"
        }), " parameter does not automatically trigger an ", _jsx(_components.code, {
          children: "identify"
        }), " event."]
      }), "\n", _jsx(_components.h2, {
        id: "autocollected-fields",
        children: "Autocollected fields"
      }), "\n", _jsxs(_components.p, {
        children: ["The browser SDK autocollects the following fields within the ", _jsx(_components.code, {
          children: "context"
        }), " of each event:"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Field"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "page.path"
              })
            }), _jsx(_components.td, {
              children: "The current page's sanitized path, i.e. with search parameters and the site domain removed."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "page.referrer"
              })
            }), _jsx(_components.td, {
              children: "The referrer to the current page. An empty string denotes a direct referrer."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "page.search"
              })
            }), _jsx(_components.td, {
              children: "The search parameter of the current page, if any"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "page.title"
              })
            }), _jsx(_components.td, {
              children: "The current page's title"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "page.url"
              })
            }), _jsx(_components.td, {
              children: "The current page's full URL"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "sessionId"
              })
            }), _jsx(_components.td, {
              children: "An identifier for the visitor's current session."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "userAgent"
              })
            }), _jsx(_components.td, {
              children: "The raw user agent string"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "userAgentData"
              })
            }), _jsx(_components.td, {
              children: "Parsed information on the user agent. This includes whether the visitor's device is a mobile device, and the platform of the device."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "locale"
              })
            }), _jsx(_components.td, {
              children: "The browser's locale"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "library"
              })
            }), _jsx(_components.td, {
              children: "Information about the SDK version used to send the event."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "timezone"
              })
            }), _jsx(_components.td, {
              children: "The visitor's local timezone."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "session-tracking",
        children: "Session tracking"
      }), "\n", _jsx(_components.p, {
        children: "A session is a group of \"uninterrupted\" events that were performed by the same visitor.\nThe session resets once the session's timeout has expired, and automatically refreshes when a new event is sent.\nFor example, if the visitor views a couple pages, and then returns to your site after a day, the next day's events will be considered a new session."
      }), "\n", _jsxs(_components.p, {
        children: ["The browser SDK automatically tracks a ", _jsx(_components.code, {
          children: "sessionId"
        }), " field to track what events occurred in the same session. If two events occured in the same session, they\nwill have the same session ID."]
      }), "\n", _jsxs(_components.p, {
        children: ["The session timeout can be adjusted from the default of ", _jsx(_components.strong, {
          children: "30 minutes"
        }), " when the SDK is initialized:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-js",
          children: ["e.", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "load"
          }), "(", _jsx(_components.span, {
            className: "hljs-string",
            children: "'YOUR_WRITE_KEY'"
          }), ", {\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "apiHost"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'us-east-1.hightouch-events.com'"
          }), ",\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "user"
          }), ": {\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "sessions"
          }), ": {\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "autoTrack"
          }), ":", _jsx(_components.span, {
            className: "hljs-literal",
            children: "true"
          }), ",\n      ", _jsx(_components.span, {
            className: "hljs-comment",
            children: "// Set the timeout to 60 minutes."
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "timeout"
          }), ": ", _jsx(_components.span, {
            className: "hljs-number",
            children: "60"
          }), "*", _jsx(_components.span, {
            className: "hljs-number",
            children: "60"
          }), "*", _jsx(_components.span, {
            className: "hljs-number",
            children: "1000"
          }), ",\n    }\n  }\n})\n"]
        })
      }), "\n", _jsx(_components.h2, {
        id: "campaign-attribution-utm",
        children: "Campaign Attribution (UTM)"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch automatically captures UTM parameters that you pass in URLs. You can also manually pass UTMs into the ", _jsx(_components.code, {
          children: "context.campaign"
        }), " field."]
      }), "\n", _jsxs(_components.p, {
        children: ["UTM parameters are only used when linking to your site from outside of your domain. When a visitor arrives to your site using a link containing UTM parameters, Hightouch's Browser SDK will automatically parse the URL query string and store UTM parameters within the ", _jsx(_components.code, {
          children: "context.campaign"
        }), " object as outlined here. These parameters do not persist to subsequent calls unless you pass them explicitly."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "UTM parameters"
        }), ":"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Parameter"
            }), _jsx(_components.th, {
              children: "Mapping"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "utm_campaign"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "campaign.name"
              })
            }), _jsxs(_components.td, {
              children: ["This is the name of your campaign. All marketing activities that support this campaign need to have the same ", _jsx(_components.code, {
                children: "utm_campaign"
              }), " so that downstream analysis to measure performance for this specific campaign can be done off this primary key."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "utm_medium"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "campaign.medium"
              })
            }), _jsxs(_components.td, {
              children: ["How the traffic is coming to your site. Is it through email, a display ad, or an online forum? This ensures downstream analysis can easily see which channel performs the best.", _jsx("br", {}), _jsx("br", {}), "eg. ", _jsx(_components.code, {
                children: "email"
              }), ", ", _jsx(_components.code, {
                children: "paid-display"
              }), ", ", _jsx(_components.code, {
                children: "paid-social"
              }), ", ", _jsx(_components.code, {
                children: "organic-social"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "utm_source"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "campaign.source"
              })
            }), _jsxs(_components.td, {
              children: ["Where the traffic is specifically coming from. You can be specific here. This ensures downstream analysis can measure which specific source brings the most conversions.", _jsx("br", {}), _jsx("br", {}), "eg. ", _jsx(_components.code, {
                children: "twitter"
              }), ", ", _jsx(_components.code, {
                children: "facebook"
              }), ", ", _jsx(_components.code, {
                children: "adroll"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "utm_content"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "campaign.content"
              })
            }), _jsxs(_components.td, {
              children: ["For multiple calls to action on a single page, ", _jsx(_components.code, {
                children: "utm_content"
              }), " indicates which one. For example, on a website, there may be three different display ads. While the link on each display ad will have the same ", _jsx(_components.code, {
                children: "utm_campaign"
              }), ", ", _jsx(_components.code, {
                children: "utm_medium"
              }), ", and ", _jsx(_components.code, {
                children: "utm_source"
              }), ", the ", _jsx(_components.code, {
                children: "utm_content"
              }), " will be different.", _jsx("br", {}), _jsx("br", {}), "eg. ", _jsx(_components.code, {
                children: "banner"
              }), ", ", _jsx(_components.code, {
                children: "left-side"
              }), ", ", _jsx(_components.code, {
                children: "bottom-side"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "utm_term"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "campaign.term"
              })
            }), _jsxs(_components.td, {
              children: ["This is the parameter suggested for paid search to identify keywords for your ad. If you’re using Google Adwords and have enabled “autotagging”, then you don’t need to worry about this. Otherwise, you can manually pass the keywords from your search terms through this parameter so that you can see which keywords convert the most. Note that this parameter is reserved explicitly for search.", _jsx("br", {}), _jsx("br", {}), "eg. ", _jsx(_components.code, {
                children: "toast"
              }), ", ", _jsx(_components.code, {
                children: "butter"
              }), ", ", _jsx(_components.code, {
                children: "jam"
              })]
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["If you’d like UTM parameters to persist in subsequent calls, you’ll need to manually set those fields in ", _jsx(_components.code, {
          children: "context.campaign"
        }), ". For example:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-js",
          children: ["htevents.", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "page"
          }), "(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"My Page\""
          }), ", {}, {\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "campaign"
          }), ": {\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "name"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"TPS Innovation Newsletter\""
          }), ",\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "source"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Newsletter\""
          }), ",\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "medium"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"email\""
          }), ",\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "term"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"tps reports\""
          }), ",\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "content"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"image link\""
          }), "\n  },\n});\n"]
        })
      }), "\n", _jsx(_components.h2, {
        id: "cookies-and-localstorage",
        children: "Cookies and LocalStorage"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch stores cookies to track information about website visitors across multiple visits."
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "htjs_user_id"
          }), ": ID tracking known or identified users as defined ", _jsx(_components.a, {
            href: "/events/event-spec#identify-events#identify-events",
            children: "here"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "htjs_anonymous_id"
          }), ": ID tracking anonymous website visitors as defined ", _jsx(_components.a, {
            href: "/events/event-spec#identify-events#identify-events",
            children: "here"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "htjs_group_id"
          }), ": ID tracking the group a user is associated with as defined ", _jsx(_components.a, {
            href: "/events/event-spec#identify-events#group-events",
            children: "here"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "htjs_sesh"
          }), ": Information about the user's current session as defined ", _jsx(_components.a, {
            href: "/events/sdks/browser#session-tracking",
            children: "here"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "In addition to tracking this information in cookies, we use localStorage to track this as well. In localStorage, we also track:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "htjs_user_traits"
          }), ": Traits defined about the user being tracked"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "htjs_group_properties"
          }), ": Properties defined about the group being tracked"]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information on measures to increase persistence of cookies, please refer to our guide on ", _jsx(_components.a, {
          href: "/events/first-party-tracking",
          children: "proxying and server-side cookies"
        }), "."]
      })]
    });
  }
}
export default MDXContent;
